import { __rest, __assign } from 'tslib';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { forwardRef } from 'react';
import { useTheme } from '../hooks/useTheme.js';
import { PaginationButton } from './components/PaginationButton.js';
import { PaginationLinkButton } from './components/PaginationLinkButton.js';
import { PaginationArrowButton } from './components/PaginationArrowButton.js';
import { PaginationArrowLinkButton } from './components/PaginationArrowLinkButton.js';
import { PaginationEllipsis } from './components/PaginationEllipsis.js';
import { usePagination } from './usePagination/usePagination.js';

var PaginationItem = function (_a) {
    var item = _a.item, size = _a.size, disabled = _a.disabled, to = _a.to, href = _a.href, handlePageChange = _a.handlePageChange;
    var selected = item.selected, p = item.page, type = item.type;
    if (type === "ellipsis")
        return jsx(PaginationEllipsis, { size: size }, p);
    var commonProps = __assign({ size: size, disabled: disabled, key: p }, (selected && { tone: "black" }));
    if (to)
        return (jsx(PaginationLinkButton, __assign({}, commonProps, { href: to(p) }, { children: p })));
    // TODO: Remove `href` in version 2
    if (href)
        return (jsx(PaginationLinkButton, __assign({}, commonProps, { href: href(p) }, { children: p })));
    return (jsx(PaginationButton, __assign({}, commonProps, { onClick: function () {
            handlePageChange(p);
        } }, { children: p })));
};
var PaginationPrev = function (_a) {
    var size = _a.size, disabled = _a.disabled, handlePageChange = _a.handlePageChange, _b = _a.page, page = _b === void 0 ? 1 : _b, to = _a.to, href = _a.href;
    var commonProps = {
        size: size,
        disabled: disabled || page <= 1,
        label: "Previous page",
    };
    if (to)
        return jsx(PaginationArrowLinkButton, __assign({}, commonProps, { href: to(page - 1) }));
    if (href)
        return jsx(PaginationArrowLinkButton, __assign({}, commonProps, { href: href(page - 1) }));
    return (jsx(PaginationArrowButton, __assign({}, commonProps, { onClick: function () {
            handlePageChange(page - 1);
        } })));
};
var PaginationNext = function (_a) {
    var size = _a.size, disabled = _a.disabled, handlePageChange = _a.handlePageChange, _b = _a.page, page = _b === void 0 ? 1 : _b, pages = _a.pages, to = _a.to, href = _a.href;
    var commonProps = {
        size: size,
        disabled: disabled || page >= pages,
        sx: { transform: "scaleX(-1)" },
        label: "Next page",
    };
    if (to)
        return jsx(PaginationArrowLinkButton, __assign({}, commonProps, { href: to(page + 1) }));
    if (href)
        return jsx(PaginationArrowLinkButton, __assign({}, commonProps, { href: href(page + 1) }));
    return (jsx(PaginationArrowButton, __assign({}, commonProps, { onClick: function () {
            handlePageChange(page + 1);
        } })));
};
var Pagination = forwardRef(function Pagination(_a, ref) {
    var _b = _a.themeKey, themeKey = _b === void 0 ? "pagination" : _b; _a.children; var hideNextButton = _a.hideNextButton, hidePrevButton = _a.hidePrevButton, includePageRanges = _a.includePageRanges, page = _a.page, pages = _a.pages, _c = _a.length, length = _c === void 0 ? 7 : _c, _d = _a.size, size = _d === void 0 ? "md" : _d, onChange = _a.onChange, to = _a.to, href = _a.href, disabled = _a.disabled, props = __rest(_a, ["themeKey", "children", "hideNextButton", "hidePrevButton", "includePageRanges", "page", "pages", "length", "size", "onChange", "to", "href", "disabled"]);
    var handleStyleProps = useTheme().handleStyleProps;
    var _e = usePagination({
        page: page,
        pages: pages,
        length: length,
        includePageRanges: includePageRanges,
    }), items = _e.items, selectedPage = _e.selectedPage;
    var forwardProps = handleStyleProps(props, {
        themeKey: themeKey,
    });
    var handlePageChange = function (p) {
        onChange && onChange(p);
    };
    return (jsxs("div", __assign({ ref: ref }, forwardProps, { children: [!hidePrevButton &&
                PaginationPrev({
                    size: size,
                    disabled: disabled,
                    page: selectedPage,
                    pages: pages,
                    to: to,
                    href: href,
                    handlePageChange: handlePageChange,
                }), items.map(function (item) {
                return PaginationItem({
                    item: item,
                    size: size,
                    disabled: disabled,
                    pages: pages,
                    to: to,
                    href: href,
                    handlePageChange: handlePageChange,
                });
            }), !hideNextButton &&
                PaginationNext({
                    size: size,
                    disabled: disabled,
                    pages: pages,
                    page: selectedPage,
                    to: to,
                    href: href,
                    handlePageChange: handlePageChange,
                })] })));
});

export { Pagination };
//# sourceMappingURL=Pagination.js.map
